import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { AxiosError } from 'axios';
import { authApiClient, normalizeApiError } from '../../../api/client/axios';
import { showSnackbar } from '../../../store/snackbarSlice';

function toSignupRequestParams(params = {}) {
  const pageSize = Number(params.limit ?? params.pageSize ?? params.size ?? 10);
  const offset = Number(params.offset ?? 0);
  const page = params.page !== undefined ? Number(params.page) : pageSize > 0 ? Math.floor(offset / pageSize) : 0;

  const query = {
    page: Number.isFinite(page) && page >= 0 ? page : 0,
    page_size: Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 10,
  };
  if (params.search) {
    query.search = params.search;
  }
  return query;
}

export const fetchPendingSignups = async (query) => {
  const { data } = await authApiClient.get('/signup-requests', { params: query.queryKey[1] });
  return data;
};

export const useGetPendingSignupsHandler = ({ searchQuery = {}, enabled = true }) => {
  const dispatch = useDispatch();
  const listParams = toSignupRequestParams(searchQuery);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['pending-signups', listParams],
    queryFn: fetchPendingSignups,
    enabled,
    refetchOnWindowFocus: enabled,
    refetchOnReconnect: enabled,
    refetchOnMount: enabled ? 'always' : false,
  });

  useEffect(() => {
    if (isError && error) {
      if (error instanceof AxiosError && error.response?.data?.message) {
        dispatch(showSnackbar({ message: error.response.data.message, severity: 'error' }));
      } else {
        const { message } = normalizeApiError(error);
        dispatch(showSnackbar({ message, severity: 'error' }));
      }
    }
  }, [dispatch, error, isError]);

  const rows = Array.isArray(data?.content) ? data.content : [];
  const total = Number(data?.total_elements ?? 0);

  return {
    pendingSignups: rows,
    totalDocuments: Number.isFinite(total) ? total : 0,
    isLoading,
  };
};

export const approveSignup = async (orgId) => {
  const { data } = await authApiClient.post('/activate', { org_id: orgId });
  return data;
};

export const useApproveSignupHandler = ({ onSuccess }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: approveSignup,
    onSuccess: (data) => {
      dispatch(showSnackbar({ message: data?.message || 'Account approved successfully.' }));
      queryClient.invalidateQueries({ queryKey: ['pending-signups'] });
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      onSuccess && onSuccess(data);
    },
    onError: (error) => {
      const { message } = normalizeApiError(error);
      dispatch(showSnackbar({ message, severity: 'error' }));
    },
  });

  return {
    onApprove: mutation.mutate,
    isPending: mutation.isPending,
    approvingOrgId: mutation.variables,
  };
};
